import type { ReactElement } from "react";
import BlogIcon from "components/icons/circle-icons/blog-icon";
import BloggerLogo from "components/icons/circle-icons/blogger-logo";
import MediumLogo from "components/icons/circle-icons/medium-logo";
import SquarespaceLogo from "components/icons/circle-icons/squarespace-logo";
import TumblrLogo from "components/icons/circle-icons/tumblr-logo";
import WixLogo from "components/icons/circle-icons/wix-logo";
import WordpressLogo from "components/icons/circle-icons/wordpress-logo";

/**
 * Get a normalized twitter handle from a string
 *
 * @param handle - The input string for normalizations
 * @returns The resulting clean twitter handle in `@handle` format
 */
export const getTwitterHandle = (handle: string): string => {
  const cleanHandle = handle.trim();

  if (cleanHandle.startsWith("@")) {
    return cleanHandle;
  }

  return `@${cleanHandle}`;
};

/**
 * Get a twitter profile URL from an input string of a user's twitter handle
 *
 * @param handle - The user's handle to get the URL for
 * @returns A URL which links to the input handle
 */
export const getTwitterUrl = (handle: string): string => {
  let cleanHandle = handle.trim();

  if (cleanHandle.startsWith("@")) {
    cleanHandle = cleanHandle.slice(1);
  }

  return `https://twitter.com/${cleanHandle}`;
};

/**
 * Get a react icon element for a specific blogging platform based on a URL
 *
 * @param blogUrl - The URL of the blog to get an icon for
 * @returns An icon element for the blog which falls back to a default
 */
export const getBlogIcon = (blogUrl = ""): ReactElement => {
  const url = blogUrl.toLowerCase() || "";

  if (url.includes("blogger")) {
    return <BloggerLogo />;
  }

  if (url.includes("medium.com")) {
    return <MediumLogo />;
  }

  if (url.includes("squarespace")) {
    return <SquarespaceLogo />;
  }

  if (url.includes("tumblr")) {
    return <TumblrLogo />;
  }

  if (url.includes("wix")) {
    return <WixLogo />;
  }

  if (url.includes("wordpress")) {
    return <WordpressLogo />;
  }

  return <BlogIcon />;
};
